// Time-bucket an ISO timestamp into one of six coarse-grained buckets used
// by the activity-history modal's grouped timeline:
//
//   TODAY        — same calendar day as `now`
//   YESTERDAY    — calendar day immediately preceding TODAY
//   THIS_WEEK    — within the last 7 days but not TODAY/YESTERDAY
//   THIS_MONTH   — same calendar month, but earlier than THIS_WEEK
//   LAST_MONTH   — the calendar month immediately preceding THIS_MONTH
//   OLDER        — anything earlier
//
// Buckets are local-time based: we read the activity's local date parts
// against the user's wall clock, which is the experience expected on a
// VN-only product. Invalid timestamps fall through to OLDER so they
// at least appear in the timeline.

export type BucketKey =
  | "TODAY"
  | "YESTERDAY"
  | "THIS_WEEK"
  | "THIS_MONTH"
  | "LAST_MONTH"
  | "OLDER";

export const BUCKET_LABELS: Record<BucketKey, string> = {
  TODAY: "Hôm nay",
  YESTERDAY: "Hôm qua",
  THIS_WEEK: "Tuần này",
  THIS_MONTH: "Tháng này",
  LAST_MONTH: "Tháng trước",
  OLDER: "Cũ hơn",
};

export const BUCKET_ORDER: readonly BucketKey[] = [
  "TODAY",
  "YESTERDAY",
  "THIS_WEEK",
  "THIS_MONTH",
  "LAST_MONTH",
  "OLDER",
];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function startOfDay(t: number): number {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Bucket an ISO timestamp. Pass `now` (defaults to {@link Date.now}) to keep
 * the function deterministic under test.
 */
export function bucketOf(createdAt: string, now: number = Date.now()): BucketKey {
  const t = Date.parse(createdAt);
  if (!Number.isFinite(t)) return "OLDER";

  const todayStart = startOfDay(now);
  const yesterdayStart = todayStart - MS_PER_DAY;
  const sevenDaysAgoStart = todayStart - 6 * MS_PER_DAY; // covers a 7-day window including today

  if (t >= todayStart) return "TODAY";
  if (t >= yesterdayStart) return "YESTERDAY";
  if (t >= sevenDaysAgoStart) return "THIS_WEEK";

  // Calendar-month comparisons. THIS_MONTH = same year+month as `now`
  // and earlier than 7 days ago. LAST_MONTH = the immediately preceding
  // calendar month.
  const tDate = new Date(t);
  const nowDate = new Date(now);
  const tYear = tDate.getFullYear();
  const tMonth = tDate.getMonth();
  const nowYear = nowDate.getFullYear();
  const nowMonth = nowDate.getMonth();

  if (tYear === nowYear && tMonth === nowMonth) {
    return "THIS_MONTH";
  }

  // Compute the calendar month that precedes (nowYear, nowMonth).
  const lastMonthYear = nowMonth === 0 ? nowYear - 1 : nowYear;
  const lastMonth = nowMonth === 0 ? 11 : nowMonth - 1;
  if (tYear === lastMonthYear && tMonth === lastMonth) {
    return "LAST_MONTH";
  }

  return "OLDER";
}
